import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { CidrInput, validateCidr } from '@/components/ui/cidr-input';
import {
  useCreateRoute,
  useUpdateRoute,
  BridgeRoute,
} from '@/lib/api/edgelink';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const routeFormSchema = z.object({
  edgeAddressRangeCidr: z.string().refine(
    (val) => validateCidr(val).isValid,
    (val) => ({ message: validateCidr(val).errorMessage || 'Invalid CIDR' })
  ),
  mappedAddressRange: z.string().optional().refine(
    (val) => !val || validateCidr(val, 'ip').isValid,
    (val) => ({ message: validateCidr(val ?? '', 'ip').errorMessage || 'Invalid IP' })
  ),
});

interface UpsertRouteDialogProps {
  bridgeId: string;
  route?: BridgeRoute;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function UpsertRouteDialog({ bridgeId, route, open, onOpenChange }: UpsertRouteDialogProps) {
  const isEditing = !!route;
  const createRoute = useCreateRoute({
    onSuccess: () => onOpenChange(false),
  });
  const updateRoute = useUpdateRoute({
    onSuccess: () => onOpenChange(false),
  });

  const form = useForm<z.infer<typeof routeFormSchema>>({
    resolver: zodResolver(routeFormSchema),
    defaultValues: {
      edgeAddressRangeCidr: route?.edgeAddressRangeCidr || '',
      mappedAddressRange: route?.mappedAddressRange || '',
    },
  });

  // Reset form when opening/closing or changing route
  React.useEffect(() => {
    if (open) {
      form.reset({
        edgeAddressRangeCidr: route?.edgeAddressRangeCidr || '',
        mappedAddressRange: route?.mappedAddressRange || '',
      });
    }
  }, [open, route, form]);

  const onSubmit = async (values: z.infer<typeof routeFormSchema>) => {
    if (isEditing && route) {
      await updateRoute.mutateAsync({
        bridgeId,
        routeId: route.id,
        data: {
          edgeAddressRangeCidr: values.edgeAddressRangeCidr,
          namespaceId: route.namespaceId, // Preserve existing namespaceId
          mappedAddressRange: values.mappedAddressRange,
        },
      });
    } else {
      await createRoute.mutateAsync({
        bridgeId,
        edgeAddressRangeCidr: values.edgeAddressRangeCidr,
        namespaceId: crypto.randomUUID(), // Generate new namespace ID
        mappedAddressRange: values.mappedAddressRange,
      });
    }
  };

  const isPending = createRoute.isPending || updateRoute.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Route' : 'Add Route'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the network range for this EdgeLink route.'
              : 'Add a new network range accessible via this EdgeLink bridge.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} >
            <div className='space-y-4 px-6'>

            <FormField
              control={form.control}
              name="edgeAddressRangeCidr"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Network Range (CIDR)</FormLabel>
                  <FormControl>
                    <CidrInput
                      value={field.value}
                      onChange={field.onChange}
                      showValidation={true}
                      />
                  </FormControl>
                  <FormDescription>
                    The local network subnet reachable through this bridge.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
              />
              <FormField
                control={form.control}
                name="mappedAddressRange"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mapped IP Address (Optional)</FormLabel>
                    <FormControl>
                      <CidrInput
                        value={field.value || ''}
                        onChange={field.onChange}
                        showValidation={true}
                        mode="ip"
                        placeholder="e.g. 10.0.0.1"
                        />
                    </FormControl>
                    <FormDescription>
                      Map the edge address to this specific IP address.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
                />

                </div>
            <DialogFooter className='flex flex-wrap flex-row justify-between gap-2'>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Route'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

